import React, {useContext, useEffect} from "react";
import {Formik} from "formik";
import {QuotationContext} from "../../../contexts/QuotationContext";
import {MtplQuotation, Quotation} from "../../../model/quotation";
import {OutputValue} from "../../../components/inputs/OutputValue";
import {StepContext} from "../../../contexts/StepContext";
import "./GreenVerificationForm.scss";
import "./GreenStepsStyles.scss";
import {dateFormat, premiumFormat} from "../../../utils/format";
import {strings} from "../../../strings/strings";
import * as log from "../../../utils/log";
import {LogFunctionalModule} from "../../../utils/log";
import {DurationMap} from "../../../model/contract";
import {StepControls} from "../../../components/Scenario/Step/StepControls";
import {ProcessingContext} from "../../../contexts/ProcessingContext";
import config from "../../../config";
import {VerificationComponent} from "../other/VerificationComponent";
import {OutputLink} from "../../../components/OutputLink";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faPen} from "@fortawesome/free-solid-svg-icons";
import {getCurrentTimeKyiv} from "../../../utils/utils";
import { useHotjar } from "../../../utils/hooks/useHotjar";
import { EventTypeKey, useTabCloseTracking } from "../forms/hooks/useTabCloseTracking";
import useSwitchToNewContent from "../../../utils/hooks/useSwitchToNewContent";
import { formatVehicleEngineType } from "../../../utils/quotation";

interface GreebVerificationFormProps {
    nextStepHandler: () => void;
    onVerify: (quotation: Quotation, paymentPageUrl: string) => void;
}

export const GreenVerificationForm = ({nextStepHandler, onVerify}: GreebVerificationFormProps) => {
    const {quotation, isQuotationEditable, type} = useContext<QuotationContext<MtplQuotation>>(QuotationContext);
    const {backStepListener} = useContext(StepContext);
    const {setProcessingOpen} = useContext(ProcessingContext);

    const {trackHotjar} = useHotjar()

    useEffect(() => {
        let event = 'green_card_osago_information';
        console.log('Google Analytics Event = ' + event);
        // @ts-ignore
        window.dataLayer.push({'event': event});

        trackHotjar("confirm-order", type)
    }, []);

    const showNewContent = useSwitchToNewContent();

    function getPrimaryInsuredFullName() {
        let result = quotation.mtplContract.primaryInsured.surname + ' ' + quotation.mtplContract.primaryInsured.givenName;
        if (quotation.mtplContract.primaryInsured.middleName !== null && quotation.mtplContract.primaryInsured.middleName !== '') {
            result = result + ' ' + quotation.mtplContract.primaryInsured.middleName;
        }
        return result;
    }

    const onBackStepClick = (formProps) => {
        log.sendLog(new Date(), LogFunctionalModule.STORE_GREEN_CARD, 'INFO',
            "VERIFICATION_FORM_BACK_HANDLER",
            [{quotation, formProps}]);
        backStepListener(quotation)
    }

    const onSubmit = (values) => {
        setProcessingOpen(true);

        const [hourKyiv, minuteKyiv] = getCurrentTimeKyiv()
        console.log("Checking selling time: Not allowed 23:00 - 01:00. Current time in Kyiv " + hourKyiv + ":" + minuteKyiv);

        // check if sales allow
        if (hourKyiv >= 23 || hourKyiv < 1) {
            window.location.replace(config.technicalGreednCardWorkUrl);
            return;
        }

        log.sendLog(new Date(), LogFunctionalModule.STORE_GREEN_CARD, 'INFO',
            "TARIFICATION_FORM_NEXT_HANDLER",
            [{quotation, values}])

        nextStepHandler();
    }

    const TerriroriesLabels = {
        ter1: "Європа",
        ter2: "Молдова, Азербайджан"
    }

    const VehicleTypesLabels = {
        DVehicleTypeGreenCardEnum_1: "A - Легковий автомобіль",
        DVehicleTypeGreenCardEnum_2: "B - Мотоцикл",
        DVehicleTypeGreenCardEnum_3: "C - Вантажний автомобіль",
        DVehicleTypeGreenCardEnum_5: "E - Автобус",
        DVehicleTypeGreenCardEnum_6: "F - Причіп або напівпричіп"
    }

    const { email } = quotation?.mtplContract?.primaryInsured || {};
    useTabCloseTracking(email, EventTypeKey.AbandonedGreencard);

    return (
        <div className="green-verification-form fluid container-fluid">
            <div className="green-verification-form__info">
                <Formik
                    initialValues={{
                        validity: dateFormat(quotation.mtplContract.startDate) + '-' + dateFormat(quotation.mtplContract.endDate),
                        duration: DurationMap[quotation.mtplContract.duration],
                        primaryInsuredFullName: getPrimaryInsuredFullName(),
                        quotation
                    }}
                    onSubmit={onSubmit}
                >
                    {() => (
                        <form className="form">
                            <div className="green-verification-form__info__general">
                                <div style={{gridArea: "title"}}>
                                    <h1>{strings.greenVerifyStep.dataCheckHeader}</h1>
                                </div>

                                <div style={{gridArea: "total"}}>
                                    <OutputValue
                                        outputValue={`${premiumFormat(quotation.totalPremium)} ${strings.currencyUAH}`}
                                        name="totalPremium" label={strings.greenVerifyStep.price}/>
                                </div>

                                <div style={{gridArea: "coverage"}}>
                                    <OutputValue name={'quotation.mtplContract.coverageTerritory'}
                                                 label={strings.greenVerifyStep.coverageTerritory}
                                                 outputValue={TerriroriesLabels[quotation.mtplContract.coverageTerritory]}
                                    />
                                </div>

                                <div style={{gridArea: "validity"}}>
                                    <OutputValue name={'validity'}
                                                 label={strings.greenVerifyStep.validity}/>
                                </div>

                                <div style={{gridArea: "duration"}}>
                                    <OutputValue name={'duration'}
                                                 label={strings.greenVerifyStep.duration}/>
                                </div>
                            </div>

                            <div className="green-verification-form__info__insured">

                                <div style={{gridArea: "title"}}>
                                    <h1>{strings.insured}</h1>
                                </div>

                                {isQuotationEditable &&
                                    <div style={{gridArea: "edit"}} className="green-verification-form__info__insured__edit">
                                        <OutputLink label={strings.edit}
                                                    id={'green-verificationForm-insured-btnEdit'}
                                                    icon={<FontAwesomeIcon icon={faPen as any}/>}
                                                    onClickListener={() => onBackStepClick(quotation)}/>
                                    </div>
                                }

                                <div style={{gridArea: "name"}}>
                                    <OutputValue name={'primaryInsuredFullName'}
                                                 label={strings.insuredFullNameLong}/>
                                </div>

                                <div style={{gridArea: "birth-date"}}>
                                    <OutputValue name={'quotation.mtplContract.primaryInsured.birthDate'}
                                                 outputValue={dateFormat(quotation.mtplContract.primaryInsured.birthDate)}
                                                 label={strings.insuredBirthDate}/>
                                </div>

                                <div style={{gridArea: "ipn"}}>
                                    <OutputValue name={'quotation.mtplContract.primaryInsured.ipn'}
                                                 label={strings.insuredIpn}/>
                                </div>

                                <div style={{gridArea: "residentCountry"}}>
                                    <OutputValue name={'quotation.mtplContract.primaryInsured.residentCountry.name'}
                                                 label={strings.personalInfoResidentCountry + ':'}/>
                                </div>

                                <div style={{gridArea: "phone"}}>
                                    <OutputValue name={'quotation.mtplContract.primaryInsured.mobileNumber'}
                                                 label={strings.insuredPhone + ':'}/>
                                </div>

                                <div style={{gridArea: "email"}}>
                                    <OutputValue name={'quotation.mtplContract.primaryInsured.email'}
                                                 label={strings.insuredEmail}/>
                                </div>

                                <div style={{gridArea: "livingAddress"}}>
                                    <OutputValue
                                        name={'quotation.mtplContract.primaryInsured.livingAddress.translitUnstructured'}
                                        label={strings.address + ':'}/>
                                </div>
                            </div>

                            <div className="green-verification-form__info__vehicle">
                                <div style={{gridArea: "title"}}>
                                    <h1>{strings.vehicle}</h1>
                                </div>

                                {isQuotationEditable &&
                                    <div style={{gridArea: "edit"}} className="green-verification-form__info__insured__edit">
                                        <OutputLink label={strings.edit}
                                                    id={'green-verificationForm-insured-btnEdit'}
                                                    icon={<FontAwesomeIcon icon={faPen as any}/>}
                                                    onClickListener={() => onBackStepClick(quotation)}/>
                                    </div>
                                }

                                <div style={{gridArea: "vehicle-type"}}>
                                    <OutputValue name={'quotation.mtplContract.vehicle.vehicleType'}
                                                 label={strings.categoryLong + ':'}
                                                 outputValue={VehicleTypesLabels[quotation.mtplContract.vehicle.vehicleType]}/>
                                </div>

                                <div style={{gridArea: "brand"}}>
                                    <OutputValue name={'quotation.mtplContract.vehicle.brand.name'}
                                                 label={strings.brandLong + ':'}/>
                                </div>


                                <div style={{gridArea: "model"}}>
                                    <OutputValue name={'quotation.mtplContract.vehicle.model.name'}
                                                 label={strings.modelLong + ':'}/>
                                </div>

                                <div style={{gridArea: "plate-number"}}>
                                    <OutputValue name={'quotation.mtplContract.vehicle.plateNumber'}
                                                 label={strings.vehiclePlateNumber2}/>
                                </div>

                                <div style={{gridArea: "vin"}}>
                                    <OutputValue name={'quotation.mtplContract.vehicle.vin'}
                                                 label={strings.vehicleVin + ':'}/>
                                </div>

                                <div style={{gridArea: "constructionYear"}}>
                                    <OutputValue name={'quotation.mtplContract.vehicle.constructionYear'}
                                                 label={strings.vehicleConstructionYearLong + ':'}/>
                                </div>
                                {showNewContent &&
                                    (<>
                                        {quotation.mtplContract.vehicle.engineType && <div style={{ gridArea: "engineType" }}>
                                            <OutputValue name={'quotation.mtplContract.vehicle.engineType'}
                                                         outputValue={formatVehicleEngineType(quotation.mtplContract.vehicle.engineType)}
                                                         label={strings.engineType + ':'} />
                                        </div>}
                                        {quotation.mtplContract.vehicle.engineCapacity && <div style={{ gridArea: "engineCapacity" }}>
                                            <OutputValue name={'quotation.mtplContract.vehicle.engineCapacity'}
                                                label={strings.engineCapacity + ':'} />
                                        </div>}
                                        {quotation.mtplContract.vehicle.batteryCapacity && <div style={{ gridArea: "batteryCapacity" }}>
                                            <OutputValue name={'quotation.mtplContract.vehicle.batteryCapacity'}
                                                label={strings.batteryCapacity + ':'} />
                                        </div>}
                                        <div style={{ gridArea: "totalWeight" }}>
                                            <OutputValue name={'quotation.mtplContract.vehicle.totalWeight'}
                                                label={strings.totalWeight + ':'} />
                                        </div>
                                        <div style={{ gridArea: "ownWeight" }}>
                                            <OutputValue name={'quotation.mtplContract.vehicle.ownWeight'}
                                                label={strings.ownWeight + ':'} />
                                        </div>
                                        <div style={{ gridArea: "numberSeating" }}>
                                            <OutputValue name={'quotation.mtplContract.vehicle.numberSeating'}
                                                label={strings.numberSeating + ':'} />
                                        </div>
                                    </>)
                                }
                            </div>
                        </form>
                    )}
                </Formik>
            </div>
            <div className="verification-form__verify">
                <VerificationComponent onVerify={onVerify} title={strings.contractGreenCardMtsbu}/>
            </div>
        </div>
    )
};
